import {
  ApiResponse,
  ApiSlugs,
  EmailDto,
  IdentityVerificationStatusDto,
  MobileNumberDto,
  UpdateUserIdentityDemographicsDto,
  UpdateUserIdentityNamesDto,
  UserAvatarFileDto,
  UserDto,
  UserId,
  UserIdentityDto,
  UserResetPasswordDto,
  UserSubscriptionHistoryResponseDto,
} from '@workspace/data-types';
import { Observable } from 'rxjs';
import { ApiCaller } from '../../../api-caller.service';
import { ApiConnector } from '../../../api-connector.abstract-service';
import { UserSettingsConnector } from './settings/user-settings.connector';

// Don't use ApiEntityStateConnector, user are handled very specifically
export class UserConnector extends ApiConnector<UserDto, UserDto, UserId> {
  constructor(apiCaller: ApiCaller, apiPrefix: string) {
    super(apiCaller, apiPrefix, ApiSlugs.USER);
  }

  settings = () => new UserSettingsConnector(this.apiCaller, this.slug, this.apiPrefix);

  me(): Observable<UserDto> {
    return this.apiCaller.getRequest<UserDto>(this.apiPrefix, `${this.slug}/me`);
  }

  patch = () => ({
    identityNames: (identity: UpdateUserIdentityNamesDto): Observable<UserIdentityDto> =>
      this.apiCaller.patchRequest(this.apiPrefix, `${this.slug}/identity/names`, identity),
    identityDemographics: (identity: UpdateUserIdentityDemographicsDto): Observable<UserIdentityDto> =>
      this.apiCaller.patchRequest(this.apiPrefix, `${this.slug}/identity/demographics`, identity),
    identityAvatar: (file: File): Observable<UserAvatarFileDto | undefined> =>
      this.apiCaller.multipartRequest(this.apiPrefix, `${this.slug}/identity-avatar`, 'post', {}, file),
    mobileNumber: (dto: MobileNumberDto): Observable<boolean> =>
      this.apiCaller.patchRequest(this.apiPrefix, `${this.slug}/contact/mobile-number`, dto),
    email: (dto: EmailDto): Observable<boolean> =>
      this.apiCaller.patchRequest(this.apiPrefix, `${this.slug}/contact/email`, dto),
    password: (password: UserResetPasswordDto): Observable<ApiResponse> =>
      this.apiCaller.patchRequest<ApiResponse>(this.apiPrefix, `${this.slug}/password`, password),
  });

  account = () => ({
    deactivate: (): Observable<ApiResponse> =>
      this.apiCaller.postRequest<ApiResponse>(this.apiPrefix, `${this.slug}/deactivate`),
    delete: (): Observable<ApiResponse> =>
      this.apiCaller.postRequest<ApiResponse>(this.apiPrefix, `${this.slug}/delete`),
    logOut: (): Observable<ApiResponse> =>
      this.apiCaller.postRequest<ApiResponse>(this.apiPrefix, `${this.slug}/logout`),
  });

  identityVerification = () => ({
    start: (): Observable<{ clientSecret: string }> =>
      this.apiCaller.postRequest(this.apiPrefix, `${this.slug}/identity/verify/start`),
    status: (): Observable<IdentityVerificationStatusDto> =>
      this.apiCaller.getRequest(this.apiPrefix, `${this.slug}/identity/verify/status`),
  });

  subscription = () => ({
    createCustomerSession: (): Observable<{ clientSecret: string | null }> =>
      this.apiCaller.postRequest(this.apiPrefix, `${this.slug}/subscription/customer-session`),
    createPortalSession: (returnUrl: string): Observable<{ url: string }> =>
      this.apiCaller.postRequest(this.apiPrefix, `${this.slug}/subscription/portal`, { returnUrl }),
    getHistory: (page = 0, pageSize = 5): Observable<UserSubscriptionHistoryResponseDto> =>
      this.apiCaller.getRequest<UserSubscriptionHistoryResponseDto>(
        this.apiPrefix,
        `${this.slug}/subscription/history?page=${page}&pageSize=${pageSize}`,
      ),
  });
}
